import { closedSettlementListInputSchema, closedSettlementListResultSchema, closedSettlementSchema, reviewClosedSettlementInputSchema, closedSettlementAuditInputSchema, closedSettlementAuditResultSchema } from "@yahshi/contracts";
import { apiErrorSchema, apiSuccessSchema, adminUserAvatarSchema, deletionRequestListInputSchema, deletionRequestListResultSchema, deletionRequestSchema, mergeCaseListInputSchema, mergeCaseListResultSchema, mergeCaseSchema, recallTouchInputSchema, recallTouchResultSchema, reviewDeletionRequestInputSchema, reviewMergeCaseInputSchema, saveUserTagInputSchema, mutateUserTagAssignmentsInputSchema, mutateUserTagAssignmentsResultSchema, securityEventListInputSchema, securityEventListResultSchema, userListQuerySchema, userListResultSchema, userRecordSchema, userTagListInputSchema, userTagListResultSchema, userTagSummarySchema, updateUserInputSchema, type DeletionRequest, type DeletionRequestListResult, type MergeCase, type MergeCaseListResult, type SecurityEventListResult, type UserTagListResult, type UserTagSummary } from "@yahshi/contracts";
import type { CloudBaseWebClient } from "../../../lib/cloudBaseWebClient";
import { M1ApiError } from "../../m1-app/data/M1AppApi";
import type { M2UserApi } from "./M2UserApi";

type Options = { client: CloudBaseWebClient; functionName?: string; onUnauthorized?: () => void | Promise<void> };

export function createCloudBaseM2UserApi(options: Options): M2UserApi {
  async function call<T>(data: Record<string, unknown>, schema: { safeParse(value: unknown): { success: boolean; data?: T } }): Promise<T> {
    let result: unknown;
    try {
      result = (await options.client.callFunction<unknown>({ name: options.functionName ?? "m2-user-admin", data, parse: true })).result;
    } catch {
      throw new M1ApiError("CLOUDBASE_FUNCTION_ERROR", "用户服务暂时不可用，请稍后重试");
    }
    const failure = apiErrorSchema.safeParse(result);
    if (failure.success) {
      if (["UNAUTHORIZED", "ADMIN_FORBIDDEN"].includes(failure.data.error.code)) await options.onUnauthorized?.();
      throw new M1ApiError(failure.data.error.code, failure.data.error.message, failure.data.error.fields);
    }
    const parsed = apiSuccessSchema(schema as never).safeParse(result);
    if (!parsed.success) throw new M1ApiError("INVALID_RESPONSE", "用户服务响应格式无效");
    return parsed.data.data as T;
  }
  return {
    listClosedSettlements: input => call({action: "listClosedSettlements", input: closedSettlementListInputSchema.parse(input)}, closedSettlementListResultSchema),
    reviewClosedSettlement: input => call({action: "reviewClosedSettlement", input: reviewClosedSettlementInputSchema.parse(input)}, closedSettlementSchema),
    listClosedSettlementAudit: input => call({action: "listClosedSettlementAudit", input: closedSettlementAuditInputSchema.parse(input)}, closedSettlementAuditResultSchema),
    listUsers(input) {
      return call({ action: "listUsers", input: userListQuerySchema.parse(input) }, userListResultSchema);
    },
    getUser(id) {
      return call({ action: "getUser", id }, userRecordSchema);
    },
    getUserAvatar(id) {
      return call({ action: "getUserAvatar", id }, adminUserAvatarSchema);
    },
    updateUser(id, input) {
      return call({ action: "updateUser", id, input: updateUserInputSchema.parse(input) }, userRecordSchema);
    },
    listMergeCases(input): Promise<MergeCaseListResult> {
      return call({ action: "listMergeCases", input: mergeCaseListInputSchema.parse(input) }, mergeCaseListResultSchema);
    },
    reviewMergeCase(input): Promise<MergeCase> {
      return call({ action: "reviewMergeCase", input: reviewMergeCaseInputSchema.parse(input) }, mergeCaseSchema);
    },
    listSecurityEvents(input): Promise<SecurityEventListResult> {
      return call({ action: "listSecurityEvents", input: securityEventListInputSchema.parse(input) }, securityEventListResultSchema);
    },
    listUserTags(input): Promise<UserTagListResult> {
      return call({ action: "listUserTags", input: userTagListInputSchema.parse(input) }, userTagListResultSchema);
    },
    saveUserTag(input): Promise<UserTagSummary> {
      return call({ action: "saveUserTag", input: saveUserTagInputSchema.parse(input) }, userTagSummarySchema);
    },
    mutateUserTagAssignments(input) {
      return call({ action: "mutateUserTagAssignments", input: mutateUserTagAssignmentsInputSchema.parse(input) }, mutateUserTagAssignmentsResultSchema);
    },
    async recordRecallTouch(input) {
      return call({ action: "recordRecallTouch", input: recallTouchInputSchema.parse(input) }, recallTouchResultSchema);
    },
    listDeletionRequests(input): Promise<DeletionRequestListResult> {
      return call({ action: "listDeletionRequests", input: deletionRequestListInputSchema.parse(input) }, deletionRequestListResultSchema);
    },
    reviewDeletionRequest(input): Promise<DeletionRequest> {
      return call({ action: "reviewDeletionRequest", input: reviewDeletionRequestInputSchema.parse(input) }, deletionRequestSchema);
    },
  };
}
